/**
 * Country map projection presets for the admin-1 region maps.
 *
 * The numbers are computed OFFLINE by scripts/generate-admin1-maps.mjs (d3-geo fitExtent
 * against an 800x600 frame over the generated {cc}-admin1.json geometry) and emitted to
 * country-map-presets.generated.json. This file is the hand-written, typed accessor.
 *
 * Rendering contract: apply the projection with the given projectionConfig and a
 * translate of `projectionConfig.translate ?? [width/2, height/2]`. center/rotate/scale
 * were derived so the country fills an 800x600 frame (react-simple-maps' default
 * translate behavior); `center` is in d3 rotated coordinates whenever `rotate` is set.
 * geoAlbersUsa (a composite projection without center/rotate) carries an explicit
 * `translate` instead of a center.
 *
 * The MAIN projection is fitted to the country's main landmass only. Fitting it to
 * every feature let one distant island drag the whole frame — mainland Portugal used
 * to render at 13% of the frame width with the Azores drawn larger than the country,
 * and mainland Spain sat in the right half above a 237px band of empty ocean. Outlying
 * territories are drawn instead in `insets`, each in its own labelled box:
 *
 *   kind: 'reprojected'  its own projection (scale + translate, never center — center
 *                        only means anything when translate is the frame centre).
 *   kind: 'composite'    drawn by the MAIN projection, which already relocated it.
 *                        geoAlbersUsa only: it puts Alaska and Hawaii below the map
 *                        itself, so those boxes are measured rather than placed.
 */

import rawPresets from './country-map-presets.generated.json';

export type CountryMapProjectionName =
  | 'geoMercator'
  | 'geoAlbersUsa'
  | 'geoAzimuthalEqualArea';

/** [x0, y0, x1, y1] in viewBox units. */
export type CountryMapRect = readonly [number, number, number, number];

export interface CountryMapProjectionConfig {
  readonly scale: number;
  readonly center?: readonly [number, number];
  readonly rotate?: readonly [number, number, number];
  /** Exact fitted translate for an 800x600 frame; when absent use [width/2, height/2]. */
  readonly translate?: readonly [number, number];
}

export type CountryMapInsetKind = 'reprojected' | 'composite';

/**
 * One territory inside an inset box, with its OWN scale and translate.
 *
 * Members of a shared box are laid out per cell rather than fitted to their
 * combined geographic extent: Ceuta and Melilla sit 2.4° apart, so fitting the
 * pair fits mostly the sea between them and draws each city about 2px across.
 */
export interface CountryMapInsetPart {
  readonly code: string;
  readonly projectionConfig: CountryMapProjectionConfig;
  readonly contentInk: CountryMapRect;
}

export interface CountryMapInset {
  /** Subregion codes drawn in this box. More than one when territories share a frame. */
  readonly codes: readonly string[];
  /** Canonical (English) territory name; the renderer localizes via the registry. */
  readonly label: string;
  readonly kind: CountryMapInsetKind;
  /** The frame drawn on screen, label band included. */
  readonly box: CountryMapRect;
  /** Union of the members' ink. */
  readonly contentInk: CountryMapRect;
  /** Absent for 'composite' — those are drawn by the main projection. */
  readonly projection?: CountryMapProjectionName;
  /** Empty for 'composite'; one entry per code otherwise. */
  readonly parts: readonly CountryMapInsetPart[];
}

export interface CountryMapPreset {
  /**
   * Served by app/api/maps/[filename]/route.ts from public/maps, carrying a
   * `?v=` content hash.
   *
   * ⚠ The version is not decoration. That route answers
   * `immutable, max-age=31536000`, so without it a browser that fetched a
   * country once keeps that geometry for a YEAR — while the presets, which
   * ride inside the JS bundle, update on the next deploy. The two then
   * disagree: adding the French overseas departments gave returning visitors
   * five labelled inset boxes with nothing drawn inside, because the preset
   * declared insets for codes their cached file did not contain.
   */
  readonly geoUrl: string;
  /** Object key inside the TopoJSON file. */
  readonly topoObject: 'admin1';
  readonly projection: CountryMapProjectionName;
  readonly projectionConfig: CountryMapProjectionConfig;
  /** Extent the main projection was fitted into. */
  readonly contentBox: CountryMapRect;
  /** Projected bounds of the main landmass. */
  readonly mainInk: CountryMapRect;
  /** Always present; empty for the 19 countries with no outlying territory. */
  readonly insets: readonly CountryMapInset[];
}

interface GeneratedInsetPart {
  readonly code: string;
  readonly scale: number;
  readonly translate: readonly number[];
  readonly contentInk: readonly number[];
}

interface GeneratedInsetEntry {
  readonly codes: readonly string[];
  readonly label: string;
  readonly kind: string;
  readonly box: readonly number[];
  readonly contentInk: readonly number[];
  readonly projection?: string;
  readonly rotate?: readonly number[];
  readonly parts?: readonly GeneratedInsetPart[];
}

interface GeneratedPresetEntry {
  /** Short content hash of {cc}-admin1.json. Cache-busts the geometry fetch. */
  readonly geoVersion: string;
  readonly projection: string;
  readonly scale: number;
  readonly center?: readonly number[];
  readonly rotate?: readonly number[];
  readonly translate?: readonly number[];
  readonly contentBox: readonly number[];
  readonly fitInk: readonly number[];
  readonly mainInk: readonly number[];
  readonly insets: readonly GeneratedInsetEntry[];
}

interface GeneratedPresetFile {
  readonly version: number;
  readonly frame: { readonly width: number; readonly height: number; readonly pad: number };
  readonly countries: Readonly<Record<string, GeneratedPresetEntry>>;
}

const PRESET_FILE = rawPresets as unknown as GeneratedPresetFile;
const GENERATED_PRESETS: Readonly<Record<string, GeneratedPresetEntry>> = PRESET_FILE.countries;

/** The frame every preset was fitted against. The renderer's viewBox must match. */
export const COUNTRY_MAP_FRAME = PRESET_FILE.frame;

const PROJECTION_NAMES: ReadonlyArray<CountryMapProjectionName> = [
  'geoMercator',
  'geoAlbersUsa',
  'geoAzimuthalEqualArea',
];

function isProjectionName(value: string): value is CountryMapProjectionName {
  return (PROJECTION_NAMES as ReadonlyArray<string>).includes(value);
}

function asPair(values: readonly number[] | undefined): readonly [number, number] | undefined {
  if (!values || values.length < 2) return undefined;
  return [values[0], values[1]];
}

function asTriple(
  values: readonly number[] | undefined
): readonly [number, number, number] | undefined {
  if (!values || values.length < 2) return undefined;
  return [values[0], values[1], values.length > 2 ? values[2] : 0];
}

function asRect(values: readonly number[] | undefined): CountryMapRect {
  if (!values || values.length < 4) return [0, 0, 0, 0];
  return [values[0], values[1], values[2], values[3]];
}

/**
 * Returns the map preset for an internal country code (case-insensitive; the internal
 * code for the United Kingdom is "uk" — "gb" is accepted and normalized). Returns null
 * for countries without a generated admin-1 map.
 */
export function getCountryMapPreset(cc: string): CountryMapPreset | null {
  const normalized = cc.trim().toLowerCase();
  const key = normalized === 'gb' ? 'uk' : normalized;
  const entry = GENERATED_PRESETS[key];
  if (!entry || !isProjectionName(entry.projection)) return null;

  const projectionConfig: CountryMapProjectionConfig = {
    scale: entry.scale,
    center: asPair(entry.center),
    rotate: asTriple(entry.rotate),
    translate: asPair(entry.translate),
  };

  const insets: CountryMapInset[] = [];
  for (const raw of entry.insets ?? []) {
    const kind: CountryMapInsetKind = raw.kind === 'composite' ? 'composite' : 'reprojected';
    const usesOwnProjection =
      kind === 'reprojected' && !!raw.projection && isProjectionName(raw.projection);
    insets.push({
      codes: raw.codes,
      label: raw.label,
      kind,
      box: asRect(raw.box),
      contentInk: asRect(raw.contentInk),
      ...(usesOwnProjection ? { projection: raw.projection as CountryMapProjectionName } : {}),
      parts: usesOwnProjection
        ? (raw.parts ?? []).map((part) => ({
            code: part.code,
            projectionConfig: {
              scale: part.scale,
              rotate: asTriple(raw.rotate),
              translate: asPair(part.translate),
            },
            contentInk: asRect(part.contentInk),
          }))
        : [],
    });
  }

  return {
    geoUrl: `/api/maps/${key}-admin1.json?v=${entry.geoVersion}`,
    topoObject: 'admin1',
    projection: entry.projection,
    projectionConfig,
    contentBox: asRect(entry.contentBox),
    mainInk: asRect(entry.mainInk),
    insets,
  };
}

/** Internal country codes that have a generated admin-1 map preset. */
export function getCountriesWithMapPresets(): string[] {
  return Object.keys(GENERATED_PRESETS).sort();
}
